import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import { Html5Qrcode } from 'html5-qrcode';
import { Button, Message, Modal } from '@arco-design/web-react';

import { parseWeruleProfileScan } from '../../utils/qrScanRoutes';

import './QrScannerModal.css';

type QrScannerModalProps = {
  visible: boolean;
  onClose: () => void;
};

function responsiveScanBox(viewfinderWidth: number, viewfinderHeight: number) {
  const edge = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.72);
  const size = Math.max(140, Math.min(240, edge));
  return { width: size, height: size };
}

export const QrScannerModal = ({ visible, onClose }: QrScannerModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCompactViewport = useMediaQuery({ maxWidth: 767.99 });
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [starting, setStarting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      await scanner.stop().catch(() => undefined);
    }
    setCameraActive(false);
  }, []);

  const handleClose = useCallback(() => {
    void stopScanner().finally(onClose);
  }, [onClose, stopScanner]);

  const startScanner = useCallback(async () => {
    const elementId = 'werule-qr-scanner-region';
    setStarting(true);
    try {
      const scanner = new Html5Qrcode(elementId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: responsiveScanBox },
        (decodedText) => {
          const route = parseWeruleProfileScan(decodedText);
          if (!route) {
            Message.warning(t('notification_messages.qr_invalid_profile'));
            return;
          }
          void stopScanner().then(() => {
            onClose();
            navigate(route);
          });
        },
        () => undefined
      );
      setCameraActive(true);
    } catch (error) {
      console.error('QR scanner failed:', error);
      Message.error(t('notification_messages.qr_camera_error'));
      await stopScanner();
    } finally {
      setStarting(false);
    }
  }, [navigate, onClose, stopScanner, t]);

  useEffect(() => {
    if (!visible) {
      void stopScanner();
      setStarting(false);
    }
  }, [visible, stopScanner]);

  return (
    <Modal
      visible={visible}
      title={t('mentor_profile.qr_scanner_modal_title')}
      onCancel={handleClose}
      alignCenter={!isCompactViewport}
      footer={
        <Button onClick={handleClose} shape="round">
          {t('mentor_profile.qr_scanner_close')}
        </Button>
      }
      className="qr-scanner-modal-dialog"
      wrapClassName={classNames('qr-scanner-modal-wrapper', {
        'qr-scanner-modal-wrapper--compact': isCompactViewport,
      })}
      style={
        isCompactViewport
          ? undefined
          : { width: 'calc(100vw - 32px)', maxWidth: 480 }
      }
    >
      <div className="qr-scanner-modal">
        <div id="werule-qr-scanner-region" className="qr-scanner-modal__region" />
        {!cameraActive ? (
          <>
            <p className="qr-scanner-modal__hint">
              {t('mentor_profile.qr_scanner_hint_idle')}
            </p>
            <Button
              shape="round"
              type="primary"
              loading={starting}
              onClick={() => void startScanner()}
              className="qr-scanner-modal__open-camera"
            >
              {t('mentor_profile.qr_scanner_open_camera')}
            </Button>
          </>
        ) : (
          <p className="qr-scanner-modal__hint">
            {t('mentor_profile.qr_scanner_hint_active')}
          </p>
        )}
      </div>
    </Modal>
  );
};
